// TracksList.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import "../App.css";

const TracksList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [runs, setRuns] = useState([]);

  useEffect(() => {
    if (!user) {
      setRuns([]);
      return;
    }

    const runsRef = collection(db, "events");
    const q1 = query(runsRef, where("userId", "==", user.uid));
    const q2 = query(runsRef, where("admins", "array-contains", user.uid));

    const runsFromUserId = new Map();
    const runsFromAdmins = new Map();

    const mergeAndUpdateRuns = () => {
      const allRunsMap = new Map();
      runsFromUserId.forEach((run, id) => {
        allRunsMap.set(id, run);
      });
      runsFromAdmins.forEach((run, id) => {
        allRunsMap.set(id, run);
      });
      setRuns(Array.from(allRunsMap.values()));
    };

    const unsubscribe1 = onSnapshot(q1, (snapshot) => {
      runsFromUserId.clear();
      snapshot.docs.forEach((docSnap) => {
        runsFromUserId.set(docSnap.id, {
          id: docSnap.id,
          ...docSnap.data(),
        });
      });
      mergeAndUpdateRuns();
    });

    const unsubscribe2 = onSnapshot(q2, (snapshot) => {
      runsFromAdmins.clear();
      snapshot.docs.forEach((docSnap) => {
        runsFromAdmins.set(docSnap.id, {
          id: docSnap.id,
          ...docSnap.data(),
        });
      });
      mergeAndUpdateRuns();
    });

    return () => {
      unsubscribe1();
      unsubscribe2();
    };
  }, [user]);

  const tracks = useMemo(() => {
    const trackMap = new Map();

    runs.forEach((run) => {
      const trackName = (run.track || run.trackName || "").trim();
      if (!trackName) return;

      const key = trackName.toLowerCase();
      const entry = trackMap.get(key) || {
        name: trackName,
        runCount: 0,
        racerIds: new Set(),
      };

      entry.runCount += 1;
      (run.racers || []).forEach((racer) => {
        if (racer.duration !== null && racer.duration !== undefined) {
          entry.racerIds.add(racer.racerId);
        }
      });

      trackMap.set(key, entry);
    });

    return Array.from(trackMap.values())
      .map((entry) => ({
        name: entry.name,
        runCount: entry.runCount,
        racerCount: entry.racerIds.size,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [runs]);

  const handleViewRuns = (trackName) => {
    navigate(`/runs-list?track=${encodeURIComponent(trackName)}`);
  };

  const handleViewRacers = (trackName) => {
    navigate(`/racers-list?track=${encodeURIComponent(trackName)}`);
  };

  return (
    <div style={{ padding: "20px" }}>
      <h2>Tracks</h2>
      {tracks.length === 0 ? (
        <p>No tracks available.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {tracks.map((track) => (
            <div
              key={track.name}
              style={{
                border: "1px solid #eee",
                borderRadius: "8px",
                padding: "12px 16px",
                backgroundColor: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column" }}>
                <strong>{track.name}</strong>
                <span style={{ color: "#777", fontSize: "0.9em" }}>
                  {track.runCount} run{track.runCount === 1 ? "" : "s"} •{" "}
                  {track.racerCount} racer{track.racerCount === 1 ? "" : "s"} with
                  results
                </span>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  className="primary-btn"
                  onClick={() => handleViewRuns(track.name)}
                >
                  View Runs
                </button>
                <button
                  className="secondary-btn"
                  onClick={() => handleViewRacers(track.name)}
                >
                  View Racers
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TracksList;
